import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workflowApi, customersApi, pensionFundsApi, caissesApi } from '../../services/api';
import {
    ArrowLeft,
    User,
    Phone,
    FileText,
    Loader2,
    Building2,
    ArrowRight,
    CheckCircle,
    X,
    AlertCircle,
} from 'lucide-react';
import type { PensionFund } from '../../types';
import { buildWorkflowStates, colorClasses } from '../../config/workflowConfig';
import { useNotification } from '../../contexts/NotificationContext';

// Sub-components
import WorkflowInfoTab from './WorkflowInfoTab';
import WorkflowDocumentsTab from './WorkflowDocumentsTab';
import WorkflowHistoryTab from './WorkflowHistoryTab';
import WorkflowActionsTab from './WorkflowActionsTab';
import WorkflowInterventionModal from './WorkflowInterventionModal';


const WorkflowCustomerDetails: React.FC = () => {
    const notify = useNotification();
    const { uuid } = useParams<{ uuid: string }>();
    const [searchParams] = useSearchParams();
    const fromState = searchParams.get('fromState');
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [activeTab, setActiveTab] = useState<'actions' | 'info' | 'caisses' | 'documents' | 'history'>('actions');
    const [comment, setComment] = useState('');
    const [selectedSubStatus, setSelectedSubStatus] = useState('');
    const [pendingTransition, setPendingTransition] = useState<number | null>(null);
    const [showInterventionModal, setShowInterventionModal] = useState(false);
    const [interventionType, setInterventionType] = useState('');
    const [interventionMessage, setInterventionMessage] = useState('');
    // demandeSuppletiveFile removed — AEIS auto-submits on state 2→3 transition
    const [isState2Submitting, setIsState2Submitting] = useState(false);

    // State 3 - Supplétive response handling
    const [isMissingDocuments, setIsMissingDocuments] = useState(false);
    const [selectedAmount, setSelectedAmount] = useState<'more' | 'less' | 'zero' | null>(null);
    const [hasOldTransfert, setHasOldTransfert] = useState<boolean | null>(null);
    const [totalAmount, setTotalAmount] = useState('');
    const [selectedPensionFund, setSelectedPensionFund] = useState('');
    const [oldTransfertAmount, setOldTransfertAmount] = useState('');
    const [pensionFundSearch, setPensionFundSearch] = useState('');

    // State 3 - Centrale response handling
    const [noCaisseInResponse, setNoCaisseInResponse] = useState(false);
    const [selectedCaisses, setSelectedCaisses] = useState<string[]>([]);
    const [caisseSearch, setCaisseSearch] = useState('');

    // State 3 - Supplétive documents confirmation
    const [showDocumentsFournisModal, setShowDocumentsFournisModal] = useState(false);

    // State 3 - Dual response validation tracking
    const [centraleValidated, setCentraleValidated] = useState(false);
    const [suppletiveValidated, setSuppletiveValidated] = useState(false);

    // State 2 - AEIS submission
    const [aeisResult, setAeisResult] = useState<{ success: boolean; message: string; screenshot?: string | null; demande_suppletive?: string } | null>(null);
    const [aeisIsSubmitting, setAeisIsSubmitting] = useState(false);

    // Fetch tasks from database to get state names
    const { data: tasksData } = useQuery({
        queryKey: ['tasks'],
        queryFn: async () => {
            const response = await workflowApi.getTasks();
            return response.data.data;
        },
    });

    // Build workflow states configuration from database tasks
    const workflowStates = useMemo(() => {
        if (!tasksData) return {};
        return buildWorkflowStates(tasksData);
    }, [tasksData]);

    // Fetch customer data
    const { data: customer, isLoading, error } = useQuery({
        queryKey: ['workflow-customer', uuid],
        queryFn: async () => {
            if (!uuid) throw new Error('No UUID provided');
            const response = await workflowApi.getCustomerWorkflowDetails(uuid);
            return response.data.data;
        },
        enabled: !!uuid,
    });

    // Fetch customer history
    const { data: historyData, isLoading: historyLoading } = useQuery({
        queryKey: ['customer-history', uuid],
        queryFn: async () => {
            if (!uuid) return [];
            const response = await workflowApi.getCustomerHistory(uuid);
            return response.data.data;
        },
        enabled: !!uuid && activeTab === 'history',
    });

    // Fetch pension funds
    const { data: pensionFundsData, isLoading: isPensionFundsLoading } = useQuery({
        queryKey: ['customer-pension-funds', uuid],
        queryFn: async () => {
            const response = await pensionFundsApi.getAll({
                search: customer?.full_name || '',
                per_page: 100,
            });
            const filtered = response.data.data.filter((pf: PensionFund) => pf.customer_uuid === uuid);
            return filtered;
        },
        enabled: !!uuid && !!customer && activeTab === 'caisses',
    });

    // Fetch caisses list (for state 3 centrale response)
    const { data: caissesList = [] } = useQuery({
        queryKey: ['caisses-list'],
        queryFn: async () => {
            const response = await caissesApi.getList();
            return response.data.data;
        },
        enabled: !!customer && (customer.state || 1) === 3,
    });

    // Fetch document flow status
    const { data: flowStatus } = useQuery({
        queryKey: ['document-flow-status', uuid],
        queryFn: async () => {
            if (!uuid) return null;
            const response = await workflowApi.getDocumentFlowStatus(uuid);
            return response.data;
        },
        enabled: !!uuid,
        refetchInterval: 5000,
    });

    // Initialize validation state from customer data (persisted values)
    useEffect(() => {
        if (customer) {
            setCentraleValidated(customer.centrale_flow_processed || false);
            setSuppletiveValidated(customer.suppletive_flow_processed || false);
        }
    }, [customer]);

    // State transition mutation
    const stateTransitionMutation = useMutation({
        mutationFn: (data: { new_state: number; new_subStatus?: string; commentaire?: string }) =>
            customersApi.updateState(uuid!, data.new_state, data.commentaire),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workflow-customer', uuid] });
            queryClient.invalidateQueries({ queryKey: ['customer-history', uuid] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['workflow-tasks'] });
            queryClient.invalidateQueries({ queryKey: ['workflow-customers'] });
            setPendingTransition(null);
            setComment('');
            setIsState2Submitting(false);
            navigate(fromState ? `/workflow?state=${fromState}` : '/workflow');
        },
        onError: (error: unknown) => {
            const errObj = error as { response?: { data?: { message?: string } }; message?: string };
            console.error('State transition error:', error);
            setIsState2Submitting(false);
            notify.error(`Erreur lors de la transition: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
        },
    });

    // Sub-status mutation
    const subStatusMutation = useMutation({
        mutationFn: (data: { subStatus: string; commentaire?: string }) =>
            customersApi.updateSubStatus(uuid!, data.subStatus),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workflow-customer', uuid] });
            queryClient.invalidateQueries({ queryKey: ['customer-history', uuid] });
            notify.success('Sous-statut mis à jour avec succès');
        },
        onError: (error: unknown) => {
            const errObj = error as { response?: { data?: { message?: string } }; message?: string };
            console.error('Sub-status update error:', error);
            notify.error(`Erreur lors de la mise à jour: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
        },
    });

    // Demande Supplétive Upload mutation
    const demandeSuppletiveMutation = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('type', 'demande_suppletive');
            formData.append('file', file);
            return customersApi.uploadDocument(uuid!, formData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workflow-customer', uuid] });
            queryClient.invalidateQueries({ queryKey: ['customer-history', uuid] });
            notify.success('Demande supplétive uploadée avec succès');
        },
        onError: (error: unknown) => {
            const errObj = error as { response?: { data?: { message?: string } }; message?: string };
            console.error('Upload error:', error);
            notify.error(`Erreur lors de l'upload: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
        },
    });

    // Intervention creation mutation
    const interventionMutation = useMutation({
        mutationFn: (data: { intervention_type: string; message: string }) =>
            workflowApi.createIntervention(uuid!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workflow-customer', uuid] });
            queryClient.invalidateQueries({ queryKey: ['customer-history', uuid] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            setShowInterventionModal(false);
            setInterventionType('');
            setInterventionMessage('');
            notify.success('Intervention créée avec succès');
        },
        onError: (error: unknown) => {
            const errObj = error as { response?: { data?: { message?: string } }; message?: string };
            console.error('Intervention creation error:', error);
            notify.error(`Erreur lors de la création de l'intervention: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
        },
    });

    // State 3 submission mutation
    const state3SubmissionMutation = useMutation({
        mutationFn: (data: {
            response_type: 'centrale' | 'suppletive';
            no_caisse?: boolean;
            caisse_ids?: number[];
            amount_category?: 'more' | 'less' | 'zero';
            suppletive_amount?: number;
            has_old_transfert?: boolean;
            old_transfert_caisse_id?: string;
            old_transfert_amount?: number;
        }) => workflowApi.submitState3Response(uuid!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workflow-customer', uuid] });
            queryClient.invalidateQueries({ queryKey: ['workflow-customers'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['customer-pension-funds', uuid] });
            navigate(fromState ? `/workflow?state=${fromState}` : '/workflow');
        },
        onError: (error: unknown) => {
            const errObj = error as { response?: { data?: { message?: string } }; message?: string };
            console.error('State 3 submission error:', error);
            notify.error(`Erreur lors de la validation: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
        },
    });

    // Handler for validating centrale response
    const handleValidateCentrale = () => {
        state3SubmissionMutation.mutate({
            response_type: 'centrale',
            no_caisse: noCaisseInResponse,
            caisse_ids: noCaisseInResponse ? [] : selectedCaisses.map(id => parseInt(id, 10)),
        });
    };

    // Handler for validating suppletive response
    const handleValidateSuppletive = () => {
        const selectedFundName = selectedPensionFund
            ? (caissesList.find((f) => f.id.toString() === selectedPensionFund)?.caisse || selectedPensionFund)
            : undefined;

        state3SubmissionMutation.mutate({
            response_type: 'suppletive',
            amount_category: selectedAmount || undefined,
            suppletive_amount: totalAmount ? parseFloat(totalAmount) : undefined,
            has_old_transfert: hasOldTransfert || undefined,
            old_transfert_caisse_id: selectedFundName,
            old_transfert_amount: oldTransfertAmount ? parseFloat(oldTransfertAmount) : undefined,
        });
    };

    const handleStateTransition = (newState: number) => {
        if (!customer) return;
        setPendingTransition(newState);
    };

    const confirmStateTransition = async () => {
        if (pendingTransition === null) return;

        stateTransitionMutation.mutate({
            new_state: pendingTransition,
            new_subStatus: selectedSubStatus || undefined,
            commentaire: comment || undefined,
        });
    };

    const handleState2Submit = async () => {
        if (!uuid) return;
        setAeisIsSubmitting(true);
        setAeisResult(null);

        try {
            const response = await customersApi.submitAeis(uuid);
            const result = response.data;

            setAeisResult(result);
        } catch (error: unknown) {
            const errObj = error as { response?: { data?: { success: boolean; message: string; screenshot?: string | null } }; message?: string };
            const errorData = errObj.response?.data;
            setAeisResult(errorData || { success: false, message: errObj.message || 'Erreur inconnue' });
        } finally {
            setAeisIsSubmitting(false);
        }
    };

    const handleCreateIntervention = () => {
        if (!interventionType || !interventionMessage.trim()) {
            notify.error('Veuillez remplir tous les champs');
            return;
        }

        interventionMutation.mutate({
            intervention_type: interventionType,
            message: interventionMessage,
        });
    };


    const getFileUrl = (filePath: string | undefined) => {
        if (!filePath) return '';
        const filesApiUrl = process.env.REACT_APP_FILES_API_URL || 'http://127.0.0.1:8010/api/files';
        return `${filesApiUrl}?path=${encodeURIComponent(filePath)}`;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-500 mb-2">Client non trouvé</p>
                {error && (
                    <p className="text-red-500 text-sm">
                        Erreur: {error instanceof Error ? error.message : 'Unknown error'}
                    </p>
                )}
            </div>
        );
    }

    const currentState = customer.state || 1;
    const stateConfig = workflowStates[currentState];
    const colorClass = colorClasses[stateConfig?.color as keyof typeof colorClasses] || colorClasses.blue;

    const tabs: Array<{ id: 'actions' | 'info' | 'caisses' | 'documents' | 'history'; label: string }> = [
        { id: 'actions', label: 'Actions' },
        { id: 'info', label: 'Informations' },
        { id: 'caisses', label: 'Caisses' },
        { id: 'documents', label: 'Documents' },
        { id: 'history', label: 'Historique' },
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header with Back Button */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <button
                        onClick={() => navigate(fromState ? `/workflow?state=${fromState}` : '/workflow')}
                        className="text-gray-600 hover:text-gray-900 flex items-center"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Retour
                    </button>
                </div>
            </div>

            {/* Customer Recap Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="flex items-center">
                            <User className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                                <p className="text-xs text-gray-500">Nom</p>
                                <p className="text-sm font-medium text-gray-900">{customer.full_name}</p>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <Building2 className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                                <p className="text-xs text-gray-500">Compagnie</p>
                                <p className="text-sm font-medium text-gray-900">{customer.company_name || 'Non renseigné'}</p>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <FileText className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                                <p className="text-xs text-gray-500">Fondation</p>
                                <p className="text-sm font-medium text-gray-900">{customer.fondation || 'Non renseigné'}</p>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <Phone className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                                <p className="text-xs text-gray-500">Téléphone</p>
                                <p className="text-sm font-medium text-gray-900">{customer.tel || 'Non renseigné'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`${
                                    activeTab === tab.id
                                        ? 'border-primary-500 text-primary-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Tab Content Sections */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Actions Tab */}
                {activeTab === 'actions' && (
                    <WorkflowActionsTab
                        customer={customer}
                        currentState={currentState}
                        getFileUrl={getFileUrl}
                        stateTransitionIsPending={stateTransitionMutation.isPending}
                        handleStateTransition={handleStateTransition}
                        isState2Submitting={isState2Submitting}
                        handleState2Submit={handleState2Submit}
                        aeisResult={aeisResult}
                        aeisIsSubmitting={aeisIsSubmitting}
                        centraleValidated={centraleValidated}
                        suppletiveValidated={suppletiveValidated}
                        noCaisseInResponse={noCaisseInResponse}
                        setNoCaisseInResponse={setNoCaisseInResponse}
                        selectedCaisses={selectedCaisses}
                        setSelectedCaisses={setSelectedCaisses}
                        caisseSearch={caisseSearch}
                        setCaisseSearch={setCaisseSearch}
                        isMissingDocuments={isMissingDocuments}
                        setIsMissingDocuments={setIsMissingDocuments}
                        selectedAmount={selectedAmount}
                        setSelectedAmount={setSelectedAmount}
                        hasOldTransfert={hasOldTransfert}
                        setHasOldTransfert={setHasOldTransfert}
                        totalAmount={totalAmount}
                        setTotalAmount={setTotalAmount}
                        selectedPensionFund={selectedPensionFund}
                        setSelectedPensionFund={setSelectedPensionFund}
                        oldTransfertAmount={oldTransfertAmount}
                        setOldTransfertAmount={setOldTransfertAmount}
                        pensionFundSearch={pensionFundSearch}
                        setPensionFundSearch={setPensionFundSearch}
                        caissesList={caissesList}
                        handleValidateCentrale={handleValidateCentrale}
                        handleValidateSuppletive={handleValidateSuppletive}
                        state3SubmissionIsPending={state3SubmissionMutation.isPending}
                        comment={comment}
                        setComment={setComment}
                        setShowInterventionModal={setShowInterventionModal}
                    />
                )}

                {/* Info Tab */}
                {activeTab === 'info' && (
                    <WorkflowInfoTab customer={customer} />
                )}

                {/* Caisses Tab */}
                {activeTab === 'caisses' && (
                    <WorkflowDocumentsTab
                        customer={customer}
                        getFileUrl={getFileUrl}
                        activeTab="caisses"
                        pensionFundsData={pensionFundsData}
                        isPensionFundsLoading={isPensionFundsLoading}
                    />
                )}

                {/* Documents Tab */}
                {activeTab === 'documents' && (
                    <WorkflowDocumentsTab
                        customer={customer}
                        getFileUrl={getFileUrl}
                        activeTab="documents"
                        pensionFundsData={pensionFundsData}
                        isPensionFundsLoading={isPensionFundsLoading}
                    />
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                    <WorkflowHistoryTab
                        historyData={historyData}
                        historyLoading={historyLoading}
                    />
                )}
            </div>

            {/* Intervention Modal */}
            <WorkflowInterventionModal
                showInterventionModal={showInterventionModal}
                interventionType={interventionType}
                interventionMessage={interventionMessage}
                interventionIsPending={interventionMutation.isPending}
                onClose={() => setShowInterventionModal(false)}
                onInterventionTypeChange={setInterventionType}
                onInterventionMessageChange={setInterventionMessage}
                onCreateIntervention={handleCreateIntervention}
            />

            {/* Documents Fournis Confirmation Modal */}
            {showDocumentsFournisModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowDocumentsFournisModal(false)}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-5 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                        <CheckCircle className="w-5 h-5 text-green-700" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Confirmation des documents</h3>
                                        <p className="text-xs text-gray-500 mt-0.5">Vérifiez avant de soumettre</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowDocumentsFournisModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <AlertCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                                    <div className="text-sm text-gray-700">
                                        <p className="font-medium text-gray-900 mb-2">Veuillez confirmer que :</p>
                                        <ul className="space-y-2 list-disc list-inside">
                                            <li>Tous les documents demandés ont été reçus</li>
                                            <li>Les documents sont complets et lisibles</li>
                                            <li>Les informations sont conformes à la demande</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600">
                                Une fois confirmé, le dossier sera marqué comme traité et passera à l'étape suivante.
                            </p>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3 rounded-b-xl">
                            <button
                                onClick={() => setShowDocumentsFournisModal(false)}
                                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={() => {
                                    setShowDocumentsFournisModal(false);
                                    handleStateTransition(4);
                                }}
                                className="px-5 py-2.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center shadow-sm transition-colors"
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Confirmer et soumettre
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* State Transition Confirmation Modal */}
            {pendingTransition !== null && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setPendingTransition(null)}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-5 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                        <ArrowRight className="w-5 h-5 text-blue-700" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Confirmer la transition</h3>
                                        <p className="text-xs text-gray-500 mt-0.5">Changement d'état du dossier</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setPendingTransition(null)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div className="px-6 py-5 space-y-5">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm text-gray-700">
                                    Vous êtes sur le point de changer l'état de{' '}
                                    <span className="font-semibold text-blue-700">{stateConfig?.label || currentState}</span> vers{' '}
                                    <span className="font-semibold text-blue-700">{pendingTransition ? (workflowStates[pendingTransition]?.label || pendingTransition) : ''}</span>
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Commentaire (optionnel)
                                </label>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors resize-none"
                                    placeholder="Ajouter un commentaire..."
                                />
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setPendingTransition(null);
                                    setComment('');
                                }}
                                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={confirmStateTransition}
                                disabled={stateTransitionMutation.isPending}
                                className="px-5 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm transition-colors"
                            >
                                {stateTransitionMutation.isPending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Transition en cours...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Confirmer la transition
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkflowCustomerDetails;
